import {useCallback} from 'react'
import {useSearchParams} from 'react-router'

import {
  QueryParam,
  parseEnumCsvParam,
  parsePositiveIntegerParam,
  parseSortDirParam,
  type SortDirection
} from '../helpers'

type ParamUpdater = (params: URLSearchParams) => void

function updateParams(prev: URLSearchParams, ...updaters: ParamUpdater[]): URLSearchParams {
  const next = new URLSearchParams(prev)
  updaters.forEach((fn) => fn(next))
  return next
}

export function useSearchParamState() {
  const [searchParams, setSearchParams] = useSearchParams()

  const readInt = (key: string, fallback: number): number =>
    parsePositiveIntegerParam(searchParams.get(key), fallback)

  const readString = (key: string): string | undefined => searchParams.get(key) ?? undefined

  const readBool = (key: string): boolean => searchParams.get(key) === 'true'

  const readSortDir = (key = QueryParam.SortDir): SortDirection | undefined =>
    parseSortDirParam(searchParams.get(key))

  const readEnumCsv = <T extends string>(key: string, allowedValues: readonly T[]): T[] =>
    parseEnumCsvParam(searchParams, key, allowedValues)

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) =>
        updateParams(
          prev,
          (p) => (value ? p.set(key, value) : p.delete(key)),
          (p) => p.delete(QueryParam.Page)
        )
      )
    },
    [setSearchParams]
  )

  const setPage = useCallback(
    (page: number, pageSize: number) => {
      setSearchParams((prev) =>
        updateParams(
          prev,
          (p) => p.set(QueryParam.Page, String(page)),
          (p) => p.set(QueryParam.PageSize, String(pageSize))
        )
      )
    },
    [setSearchParams]
  )

  const setSort = useCallback(
    (sortBy: string, sortDir: SortDirection) => {
      setSearchParams((prev) =>
        updateParams(
          prev,
          (p) => p.set(QueryParam.SortBy, sortBy),
          (p) => p.set(QueryParam.SortDir, sortDir),
          (p) => p.delete(QueryParam.Page)
        )
      )
    },
    [setSearchParams]
  )

  return {
    readInt,
    readString,
    readBool,
    readSortDir,
    readEnumCsv,
    setParam,
    setPage,
    setSort,
    setSearchParams
  }
}
